// Centralised design tokens for the AAMPL MRM UI.
// CSS custom properties with the same names live in index.css so both JS and
// stylesheets read from one source of truth.

export const colors = {
  bg: '#F8FAFF',
  card: '#FFFFFF',
  text: '#172554',
  muted: '#64748B',
  border: '#E6EBF5',
  primary: '#4F6BFF',
  primaryLight: '#EEF3FF',
  green: '#22C55E',
  greenLight: '#ECFDF5',
  orange: '#F59E0B',
  orangeLight: '#FFF7E8',
  red: '#EF4444',
  redLight: '#FFF1F2',
  purple: '#8B5CF6',
  purpleLight: '#F3EEFF',
  cyan: '#06B6D4',
  cyanLight: '#ECFEFF',
  blue: '#3B82F6',
  blueLight: '#EFF6FF',
};

export const gradients = {
  primary: 'linear-gradient(135deg, #4F6BFF, #8B5CF6)',
  activeNav: 'linear-gradient(90deg, #EEF3FF, #F3EEFF)',
};

export const shadows = {
  card: '0 8px 30px rgba(30, 41, 59, 0.06)',
  cardHover: '0 12px 35px rgba(30, 41, 59, 0.10)',
  glow: (hex, a = 0.25) => `0 10px 30px ${hexToRgba(hex, a)}`,
};

export const radius = { card: 18, kpi: 20, button: 12, pill: 999 };

export const transitions = { fast: '150ms ease', base: '200ms ease', slow: '250ms ease' };

// Accent definitions shared by KPI cards, section cards, icon chips and pills.
// Each tone maps to: solid accent, soft (pastel) background, deep text colour,
// the resting card gradient and the "filled" gradient used when a KPI is active.
export const tones = {
  green:  { accent: '#22C55E', soft: '#ECFDF5', deep: '#15803D', ring: '#BBF7D0', rest: 'linear-gradient(135deg, #FFFFFF, #F0FDF4)', fill: 'linear-gradient(135deg, #ECFDF5, #DFFBEA)' },
  blue:   { accent: '#3B82F6', soft: '#EFF6FF', deep: '#1D4ED8', ring: '#BFDBFE', rest: 'linear-gradient(135deg, #FFFFFF, #EFF6FF)', fill: 'linear-gradient(135deg, #EFF6FF, #DBEAFE)' },
  orange: { accent: '#F59E0B', soft: '#FFF7ED', deep: '#C2410C', ring: '#FDE68A', rest: 'linear-gradient(135deg, #FFFFFF, #FFF7ED)', fill: 'linear-gradient(135deg, #FFF7E8, #FEF0D6)' },
  red:    { accent: '#EF4444', soft: '#FFF1F2', deep: '#DC2626', ring: '#FECDD3', rest: 'linear-gradient(135deg, #FFFFFF, #FFF1F2)', fill: 'linear-gradient(135deg, #FFF1F2, #FFE4E6)' },
  purple: { accent: '#8B5CF6', soft: '#F3EEFF', deep: '#6D28D9', ring: '#DDD6FE', rest: 'linear-gradient(135deg, #FFFFFF, #F3EEFF)', fill: 'linear-gradient(135deg, #F3EEFF, #EDE9FE)' },
  cyan:   { accent: '#06B6D4', soft: '#ECFEFF', deep: '#0E7490', ring: '#A5F3FC', rest: 'linear-gradient(135deg, #FFFFFF, #ECFEFF)', fill: 'linear-gradient(135deg, #ECFEFF, #CFFAFE)' },
  indigo: { accent: '#4F6BFF', soft: '#EEF3FF', deep: '#3730A3', ring: '#C7D2FE', rest: 'linear-gradient(135deg, #FFFFFF, #EEF3FF)', fill: 'linear-gradient(135deg, #EEF3FF, #E0E7FF)' },
  slate:  { accent: '#94A3B8', soft: '#F1F5F9', deep: '#475569', ring: '#E2E8F0', rest: 'linear-gradient(135deg, #FFFFFF, #F8FAFC)', fill: 'linear-gradient(135deg, #F1F5F9, #E2E8F0)' },
};

// Map legacy status keywords (used throughout the app) onto a tone.
export function toneForStatus(status) {
  const s = (status || '').toLowerCase();
  if (['green', 'closed', 'done', 'on_track', 'achieved', 'valid', 'submitted', 'approved', 'uploaded', 'success', 'active', 'a'].includes(s)) return 'green';
  if (['amber', 'pending', 'at_risk', 'expiring_soon', 'draft', 'open', 'awaiting', 'b'].includes(s)) return 'orange';
  if (['in_progress', 'data_entry', 'processing', 'running'].includes(s)) return 'indigo';
  if (['red', 'overdue', 'missed', 'expired', 'failed', 'critical', 'error', 'inactive', 'c', 'd'].includes(s)) return 'red';
  return 'slate';
}

export function hexToRgba(hex, a = 1) {
  const h = hex.replace('#', '');
  const n = parseInt(h.length === 3 ? h.split('').map((c) => c + c).join('') : h, 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`;
}

export const MONTHS = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
